import { RJSFSchema, UiSchema } from "@rjsf/utils"
import { ALL_CRITERIA_TYPES, Question } from "../calculator"

export const criteriaFormSchema = (questions: Question[]): RJSFSchema => {
  const schemas: RJSFSchema[] = []
  for (let i = 0; i < 5; i++) {
    schemas.push({
      title: "Criteria",
      type: "object",
      properties: {
        criteriaType: {
          type: "string",
          title: "Criteria Type",
          oneOf:
            ALL_CRITERIA_TYPES.map((criteriaType) => ({ const: criteriaType, title: criteriaType }))
        },
        criteriaValue: { type: "string", title: "Criteria Value" },
        criteriaQuestions: {
          type: "array",
          title: "Criteria Questions",
          items: {
            type: "object",
            properties: {
              questionId: {
                type: "string",
                title: "Question ID",
                oneOf: questions.map((question) => ({ const: question.questionId, title: question.questionId }))
              },
              validValue: { type: "string", title: "Valid Value" },
              criteriaType: {
                type: "string",
                title: "Criteria Type",
                oneOf:
                  ALL_CRITERIA_TYPES.map((criteriaType) => ({ const: criteriaType, title: criteriaType }))
              }
            }
          }
        },
      }
    })
  }

  const schema: RJSFSchema = schemas.reduce((acc, schema) => {
    schema.properties!.childCriteria = {
      type: "array",
      title: "Child Criteria",
      items: acc
    }

    return schema
  })

  return schema
}

export const calculatorFormSchema = (variant: "add" | "edit", questions: Question[]): RJSFSchema => {
  const schema: RJSFSchema = {
    title: "Calculator",
    type: "object",
    properties: {
      display: { type: "boolean", title: "Display" },
      classification: { type: "string", title: "Classification" },
      ichdUrl: { type: "string", title: "ICHD URL" },
      parentCalculatorId: { type: ["string", 'null'], title: "Parent Calculator ID" },
      criteria: {
        type: "array",
        title: "Criteria",
        items: criteriaFormSchema(questions)
      }
    },
    required: ["classification", "ichdUrl"]
  }

  if (variant === "add") {
    schema.properties!.calculatorId = { type: "string", title: "Calculator ID" }
    schema.required!.push("calculatorId")
  }

  return schema
}

export const criteriaUiSchema = (): UiSchema => {

  const questionUiSchema: UiSchema = {
    questionId: {
      "ui:description": "questionId of the question."
    },
    validValue: {
      "ui:description": "Value that should be compared to the answer using the criteria type. Should be single number for all criteria types except 'range'. For range use 'min_num-max_num'"
    },
    criteriaType: {
      "ui:description": "Type of criteria to apply. 'Equal' checks that answer value is validValue. 'Lower' checks that answer value is less than or equal to validValue. 'Higher' checks that answer value is greater than or equal to validValue. 'Range' checks that answer value is between min_num and max_num."
    }
  }


  const criteriaUiSchemaList: UiSchema[] = []
  for (let i = 0; i < 5; i++) {
    criteriaUiSchemaList.push({
      criteriaType: {
        "ui:description": "Type of criteria to apply. 'Equal' checks that answer value is validValue. 'Lower' checks that answer value is less than or equal to validValue. 'Higher' checks that answer value is greater than or equal to validValue. 'Range' checks that answer value is between min_num and max_num."
      },
      criteriaValue: {
        "ui:description": "Value that should be compared to the number of valid criteria/questions using the criteria type. Should be single number for all criteria types except 'range'. For range use 'min_num-max_num'"
      },
      criteriaQuestions: {
        "ui:description": "Add as many questions as you need.",
        items: questionUiSchema
      },
      childCriteria: {
        "ui:description": "Add as many child criteria as you need."
      }
    })
  }

  const uiSchema: UiSchema = criteriaUiSchemaList.reduce((acc, schema) => {
    schema['childCriteria']['items'] = acc
    return schema
  })

  return uiSchema
}

export const calculatorUiSchema = (): UiSchema => {
  return {
    display: {
      "ui:description": "Whether to display the calculator to users."
    },
    classification: {
      "ui:description": "ICHD-3 classification the calculator is for."
    },
    ichdUrl: {
      "ui:description": "URL to the ICHD-3 classification."
    },
    parentCalculatorId: {
      "ui:description": "calculatorId of the parent calculator. Leave blank if this is calculator is not built on top of another calculator."
    },
    criteria: {
      "ui:description": "Add as many criteria as you need. Criteria can have child criteria and questions.",
      items: criteriaUiSchema()
    },
  }
}
